import { Box, CircularProgress } from "@mui/material";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useToast } from "../lib";
import { logError, logEvent } from "../lib/analytics";
import { APP_V2_APP_EVENTS } from "../lib/analytics/events";
import { useZendeskArticle } from "./useZendeskArticle";
import { ZendeskArticleContext } from "./zendesk-article-error-context";

export function ZendeskSsoBridge() {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("return_to");
  const kind = searchParams.get("kind");
  const message = searchParams.get("message"); // This is the Zendesk-provided message
  const { openArticle } = useZendeskArticle();
  const { showErrorToast } = useToast();

  useEffect(() => {
    async function redirect() {
      if (kind === "info" && message === "You have been signed out.") {
        logEvent(APP_V2_APP_EVENTS.ZENDESK_USER_SIGNOUT);
      } else if (kind === "error" && message) {
        logError(APP_V2_APP_EVENTS.ZENDESK_ARTICLE_OPENED_ERROR, {
          error: new Error(`Zendesk SSO Callback Error: ${message}`),
          metadata: {
            context: ZendeskArticleContext.ZENDESK_SSO_CALLBACK_ERROR,
            zendeskCallbackMessage: message,
            articleLink: returnTo ?? undefined,
          },
        });
        showErrorToast("Couldn't open article. Try again.");
      } else if (returnTo) {
        // NOTE: This component is used when user opens a Zendesk article from the external sources.
        //       We need to open the article in the same tab to avoid new tab being opened.
        //       Some browser may block new tab or ask for permission to open new tab, this creates unnecessary friction.
        await openArticle(returnTo, { browserOpenOptions: { windowName: "_self" } });
      } else {
        logError(APP_V2_APP_EVENTS.ZENDESK_ARTICLE_OPENED_ERROR, {
          error: new Error("Missing return_to parameter in Zendesk SSO Bridge"),
          metadata: {
            context: ZendeskArticleContext.ZENDESK_SSO_BRIDGE_MISSING_RETURN_TO_URL,
            // No articleLink here as returnTo is missing
          },
        });
        showErrorToast("We cannot find the article you're looking for. Please contact support.");
      }

      history.push("/home");
    }

    void redirect();
  }, [returnTo, openArticle, history, location.search, kind, message, showErrorToast]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress size={40} />
      <Title component="h1" variant="h5">
        Hang on!
      </Title>
      <Text variant="body1">We&apos;re redirecting you to your destination...</Text>
    </Box>
  );
}
